import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import {
  Container,
  Grid,
  IconButton,
  TextField,
  Typography,
  Backdrop,
  Box,
  Button,
  FormControlLabel,
  Modal,
  Switch,
} from "@mui/material";

import { postNotification } from "../../../components/utilities/Notifications";
import ModalLoadingFull from "../../../components/modals/ModalLoadingFull";

import {
  BCPSquarePlus,
  BCPXmark,
} from "../../../components/icons/bcp/BCPIcons";

import { handlePagingAll } from "../../../components/api/APIUtilities";
import ListResponsive from "../../../components/other/ListResponsive";
import { AdminTabs } from "../Admin";
import GenericPicker from "../../../components/utilities/GenericPicker";
import { postData } from "../../../components/utilities/UtilityFunctions";

export default function AdminEventRoles(props) {
  const { activeTab, handleTabChange, authContext } = props;
  const { t } = useTranslation("translation", {
    keyPrefix: "pages.admin"
  });

  const [processing, setProcessing] = useState(false);
  const [subLoading, setSubLoading] = useState(true);

  const [roles, setRoles] = useState(null);
  const [role, setRole] = useState(null);

  useEffect(() => {
    const check = async () => {
      var apiRoles = await handlePagingAll(`/eventroles`, {
        limit: 100,
      });
      apiRoles.sort((a, b) => {
        if (
          a.orderNum !== undefined &&
          b.orderNum !== undefined &&
          a.orderNum !== b.orderNum
        ) {
          return a.orderNum - b.orderNum;
        }

        var roleA = a.name.toLowerCase();
        var roleB = b.name.toLowerCase();
        if (roleA > roleB) return 1;
        if (roleA < roleB) return -1;

        return 0;
      });
      setRoles(apiRoles);
      setSubLoading(false);
    };
    check();
  }, []);

  const roleLabel = (row) => {
    var roles = [];
    if (row.isOrganizer) {
      roles.push("Organizer");
    }
    if (row.isJudge) {
      roles.push("Judge");
    }
    return roles.join(", ");
  };

  return (
    <Container sx={{ pt: 2, pb: 2 }} maxWidth="lg">
      <Grid container sx={{ gap: 2 }}>
        <Grid
          item
          xs={12}
          sx={{
            p: 2,
            borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
            borderRadius: 1,
            boxShadow: 1,
            backgroundColor: "background.secondary",
          }}
        >
          <Typography variant="h3">{t("adminSettings")}</Typography>

          <AdminTabs
            activeTab={activeTab}
            handleTabChange={handleTabChange}
            authContext={authContext}
          />
        </Grid>

        <Grid
          item
          xs={12}
          sx={{
            p: 2,
            borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
            borderRadius: 1,
            boxShadow: 1,
            backgroundColor: "background.secondary",
          }}
        >
          <ListResponsive
            originalList={subLoading ? null : roles}
            doSearch
            doPages
            initialRowsPerPage={24}
            actionButton={[
              {
                props: {
                  variant: "contained",
                  color: "primary",
                  onClick: () => {
                    setRole({});
                  },
                  startIcon: <BCPSquarePlus />,
                },
                label: "Add Event Role",
              },
            ]}
            searchProps={{
              label: "Search Event Roles",
              performSearch: (originalList, searchText) => {
                if (!searchText || searchText === "") {
                  return originalList;
                } else {
                  var filteredList = originalList.filter(function (el) {
                    if (el?.name?.toLowerCase().includes(searchText))
                      return true;

                    return false;
                  });
                  return filteredList;
                }
              },
            }}
            rowProps={{
              mapFunction: (row, index) => (
                <Grid
                  key={index}
                  item
                  xs={12}
                  onClick={() => {
                    setRole(row);
                  }}
                  sx={{
                    p: 1,
                    mb: 1,
                    border: "1px solid rgba(0, 0, 0, 0.12)",
                    borderRadius: 1,
                    boxShadow: 1,
                    "&:hover": {
                      cursor: "pointer",
                    },
                  }}
                >
                  <Typography variant="body1">
                    {`${row.orderNum}: ${row.name}`}
                  </Typography>
                  <Typography variant="caption">{roleLabel(row)}</Typography>
                </Grid>
              ),
            }}
          />
        </Grid>
      </Grid>

      <RoleModal
        role={role}
        setRole={setRole}
        roles={roles}
        setRoles={setRoles}
        setProcessing={setProcessing}
      />

      {Boolean(processing) && <ModalLoadingFull />}
    </Container>
  );
}

const permissionTypes = [
  { value: "eventDetails", label: "Event Details" },
  { value: "eventUsers", label: "Event Users" },
  { value: "circuitSubmission", label: "Circuit Submission" },
  { value: "judgeReport", label: "Judge Report" },
];

const getPermissionValue = (value) => {
  return value === undefined ? 0 : value ? 2 : 1;
};
const getPermissionBoolean = (value) => {
  return value === 0 ? undefined : value === 2;
};

function RoleModal(props) {
  const { role, setRole, roles, setRoles, setProcessing } = props;

  return (
    <Modal
      disableAutoFocus
      open={Boolean(role)}
      onClose={() => {
        setRole(null);
      }}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
      sx={{ zIndex: 9999 }}
    >
      {role ? (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: ["95vw", "85vw", 600],
            maxHeight: "95vh",
            overflowY: "scroll",
            bgcolor: "background.paper",
            border: "1px solid rgba(0, 0, 0, 0.12)",
            borderRadius: 1,
            boxShadow: 24,
            p: 4,
          }}
        >
          <IconButton
            aria-label="cancel"
            color="error"
            sx={{ position: "absolute", top: "4px", right: "4px" }}
            onClick={() => {
              setRole(null);
            }}
          >
            <BCPXmark />
          </IconButton>
          <Grid
            container
            spacing={2}
            sx={{
              pb: 1,
              mb: 2,
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
            }}
          >
            <Grid item xs>
              <Typography variant="h4" component="h2">
                {role.id ? "Update Role" : "Create Role"}
              </Typography>
            </Grid>
          </Grid>
          <Formik
            initialValues={{
              id: role?.id || null,
              name: role?.name || "",
              orderNum: role?.orderNum !== undefined ? role?.orderNum : "",
              public: role?.public !== undefined ? role.public : false,
              isOrganizer:
                role?.isOrganizer !== undefined ? role.isOrganizer : false,
              isJudge: role?.isJudge !== undefined ? role.isJudge : false,
              permissions: {
                eventDetails: getPermissionValue(
                  role?.permissions?.eventDetails
                ),

                eventUsers: getPermissionValue(role?.permissions?.eventUsers),

                circuitSubmission: getPermissionValue(
                  role?.permissions?.circuitSubmission
                ),

                judgeReport: getPermissionValue(role?.permissions?.judgeReport),
              },
            }}
            enableReinitialize
            validationSchema={Yup.object().shape({
              name: Yup.string()
                .min(3, "Minimum 3 Characters!")
                .max(255, "Maximum 255 Characters!")
                .required("Required"),
              orderNum: Yup.number().min(0, "Minimum 0").required("Required"),
            })}
            onSubmit={async (values) => {
              var apiData;
              setProcessing(true);
              try {
                var sanitizedValues = {
                  name: values.name,
                  orderNum: values.orderNum,
                  public: values.public,
                  isOrganizer: values.isOrganizer,
                  isJudge: values.isJudge,
                  permissions: {},
                };

                Object.keys(values.permissions).forEach((permission) => {
                  var permissionValue = getPermissionBoolean(
                    values.permissions[permission]
                  );
                  if (permissionValue !== undefined) {
                    sanitizedValues.permissions[permission] = permissionValue;
                  }
                });

                if (values.id) {
                  var body = {
                    set: sanitizedValues,
                  };
                  apiData = await postData(`/eventroles/${values.id}`, {
                    body: body,
                  });
                  postNotification("success", "Event Role Updated");
                } else {
                  apiData = await postData(`/eventroles`, {
                    body: sanitizedValues,
                  });
                }

                var newRoles;
                if (role.id) {
                  newRoles = [];
                  roles.forEach((currentRole) => {
                    if (currentRole.id !== role.id) {
                      newRoles.push(currentRole);
                    } else {
                      newRoles.push(apiData);
                    }
                  });
                } else {
                  newRoles = [apiData, ...roles];
                }
                setRoles(newRoles);
                setRole(null);
                setProcessing(false);
                return;
              } catch (err) {
                postNotification("error", err);
                setProcessing(false);
              }
            }}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              touched,
              values,
              setFieldValue,
            }) => (
              <form onSubmit={handleSubmit}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <TextField
                      name="name"
                      label="Name"
                      value={values.name}
                      variant="outlined"
                      fullWidth
                      required
                      margin="normal"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      error={Boolean(touched.name && errors.name)}
                      helperText={touched.name && errors.name}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="orderNum"
                      label="Order"
                      value={values.orderNum}
                      variant="outlined"
                      fullWidth
                      required
                      margin="normal"
                      type="number"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      error={Boolean(touched.orderNum && errors.orderNum)}
                      helperText={touched.orderNum && errors.orderNum}
                      inputProps={{
                        min: 0,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      label="Public Role"
                      sx={{
                        ml: 0,
                        width: "100%",
                        "& .MuiFormControlLabel-label": {
                          width: "calc(100% - 58px)",
                        },
                      }}
                      control={
                        <Switch
                          checked={values.public}
                          onChange={(e) => {
                            setFieldValue("public", !values.public);
                          }}
                        />
                      }
                      labelPlacement="start"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography sx={{ fontWeight: "600" }}>
                      Required User System Roles
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      label="Organizer"
                      sx={{
                        ml: 0,
                        width: "100%",
                        "& .MuiFormControlLabel-label": {
                          width: "calc(100% - 58px)",
                        },
                      }}
                      control={
                        <Switch
                          checked={values.isOrganizer}
                          onChange={(e) => {
                            setFieldValue("isOrganizer", !values.isOrganizer);
                          }}
                        />
                      }
                      labelPlacement="start"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      label="Judge"
                      sx={{
                        ml: 0,
                        width: "100%",
                        "& .MuiFormControlLabel-label": {
                          width: "calc(100% - 58px)",
                        },
                      }}
                      control={
                        <Switch
                          checked={values.isJudge}
                          onChange={(e) => {
                            setFieldValue("isJudge", !values.isJudge);
                          }}
                        />
                      }
                      labelPlacement="start"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography sx={{ fontWeight: "600" }}>
                      Event Permissions
                    </Typography>
                  </Grid>
                  {permissionTypes.map((permission) => {
                    return (
                      <Grid item xs={12}>
                        <GenericPicker
                          label={permission.label}
                          required
                          inclAny={false}
                          name={`permissions[${permission.value}]`}
                          list={[
                            { value: 0, label: "No Access" },
                            { value: 1, label: "Read Only" },
                            { value: 2, label: "Full Access" },
                          ]}
                          value={values.permissions[permission.value]}
                          handleChange={handleChange}
                        />
                      </Grid>
                    );
                  })}

                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        setRole(null);
                      }}
                    >
                      Cancel
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="success"
                      type="submit"
                      disabled={!values.name}
                    >
                      Save
                    </Button>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </Box>
      ) : (
        <></>
      )}
    </Modal>
  );
}
